import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { listPricingStrategies, updatePricingStrategies, testPricingPrompt } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { useAuth } from "../../lib/auth";

export function PricingStrategiesSection() {
    const { user } = useAuth();
    const isReviewer = user?.role === "reviewer";
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // iter-72: per-card test state. Each map is keyed by strategy id:
    //   { [id]: { running: bool, result: {grand_total, lines...} | null, defaultResult: same | null }}
    const [testState, setTestState] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const d = await listPricingStrategies();
                setRows(d?.strategies || []);
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Couldn't load strategies");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const updateField = (id, field, val) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
    };

    const resetOne = (id) => {
        setRows((prev) =>
            prev.map((r) =>
                r.id === id
                    ? {
                          ...r,
                          label: r.default_label,
                          hint: r.default_hint,
                          prompt: r.default_prompt,
                      }
                    : r,
            ),
        );
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = rows.map((r) => ({
                id: r.id,
                label: r.label,
                hint: r.hint,
                prompt: r.prompt,
            }));
            const d = await updatePricingStrategies(payload);
            setRows(d?.strategies || []);
            toast.success("Pricing strategies saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const runTest = async (row, useDefault = false) => {
        setTestState((prev) => ({
            ...prev,
            [row.id]: { ...(prev[row.id] || {}), running: true, usingDefault: useDefault },
        }));
        try {
            const data = await testPricingPrompt({
                strategy_id: row.id,
                prompt: useDefault ? row.default_prompt : row.prompt,
            });
            setTestState((prev) => {
                const curr = prev[row.id] || {};
                const next = { ...curr, running: false };
                if (useDefault) {
                    next.defaultResult = data;
                } else {
                    next.result = data;
                }
                return { ...prev, [row.id]: next };
            });
            toast.success(
                `Test ran — grand total $${Number(data.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            );
        } catch (e) {
            setTestState((prev) => ({
                ...prev,
                [row.id]: { ...(prev[row.id] || {}), running: false },
            }));
            toast.error(
                e?.response?.data?.detail || "Test run failed — check backend logs",
            );
        }
    };

    if (loading)
        return (
            <div className="text-sm text-slate-500" data-testid="admin-prompts-loading">
                Loading strategies…
            </div>
        );

    return (
        <div className="space-y-4" data-testid="admin-prompts-section">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div className="max-w-2xl">
                    <h3 className="text-white font-bold text-lg">Pricing strategies</h3>
                    <p className="text-sm text-slate-400 mt-1">
                        The three radio options users see on the Estimate
                        dialog. Rename the buttons and rewrite the prompt
                        the AI executes — the current value is shown so
                        you can tweak incrementally. Saved changes apply
                        to every future estimate generated by any user.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving || isReviewer}
                    data-testid="admin-prompts-save"
                    className="px-4 py-2 text-sm font-bold rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white disabled:opacity-50 transition-colors"
                >
                    {saving ? "Saving…" : isReviewer ? "Read-only" : "Save all"}
                </button>
            </div>

            {isReviewer && (
                <div
                    className="rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-200"
                    data-testid="admin-prompts-reviewer-notice"
                >
                    Pricing strategies are read-only for reviewer accounts.
                </div>
            )}

            <div className="space-y-4">
                {rows.map((r) => {
                    const dirty =
                        r.label !== r.default_label ||
                        r.hint !== r.default_hint ||
                        (r.prompt || "") !== (r.default_prompt || "");
                    return (
                        <div
                            key={r.id}
                            data-testid={`admin-prompt-card-${r.id}`}
                            className="border border-[#2A3655] rounded-sm bg-[#0F1B3A] p-4"
                        >
                            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                                        Strategy id
                                    </span>
                                    <span className="px-2 py-0.5 rounded-sm bg-[#1C253C] border border-[#2A3655] text-[11px] font-mono text-[#00A3FF]">
                                        {r.id}
                                    </span>
                                    {dirty && (
                                        <span className="px-2 py-0.5 rounded-sm bg-amber-500/15 border border-amber-500/40 text-[10px] uppercase tracking-wider font-bold text-amber-300">
                                            Modified
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => resetOne(r.id)}
                                    disabled={isReviewer}
                                    data-testid={`admin-prompt-reset-${r.id}`}
                                    className="text-xs text-slate-400 hover:text-white underline-offset-2 hover:underline disabled:opacity-40 disabled:hover:no-underline disabled:cursor-not-allowed"
                                >
                                    Reset to default
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">
                                        Button label
                                    </label>
                                    <input
                                        type="text"
                                        maxLength={40}
                                        value={r.label || ""}
                                        onChange={(e) => updateField(r.id, "label", e.target.value)}
                                        readOnly={isReviewer}
                                        data-testid={`admin-prompt-label-${r.id}`}
                                        className="w-full px-3 py-2 bg-[#1C253C] border border-[#2A3655] rounded-sm text-white text-sm outline-none focus:border-[#00A3FF] read-only:opacity-70 read-only:cursor-not-allowed"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">
                                        Hint line
                                    </label>
                                    <input
                                        type="text"
                                        maxLength={140}
                                        value={r.hint || ""}
                                        onChange={(e) => updateField(r.id, "hint", e.target.value)}
                                        readOnly={isReviewer}
                                        data-testid={`admin-prompt-hint-${r.id}`}
                                        className="w-full px-3 py-2 bg-[#1C253C] border border-[#2A3655] rounded-sm text-white text-sm outline-none focus:border-[#00A3FF] read-only:opacity-70 read-only:cursor-not-allowed"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">
                                    Prompt sent to the AI when this strategy is picked
                                </label>
                                <textarea
                                    rows={8}
                                    value={r.prompt || ""}
                                    onChange={(e) => updateField(r.id, "prompt", e.target.value)}
                                    readOnly={isReviewer}
                                    data-testid={`admin-prompt-text-${r.id}`}
                                    className="w-full px-3 py-2 bg-[#1C253C] border border-[#2A3655] rounded-sm text-slate-100 text-[12px] font-mono leading-relaxed outline-none focus:border-[#00A3FF] resize-y read-only:opacity-70 read-only:cursor-not-allowed"
                                    placeholder="Prompt block — supports markdown"
                                />
                            </div>

                            <TestPromptPanel
                                row={r}
                                state={testState[r.id] || {}}
                                onRun={runTest}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Run-an-actual-Claude-call panel per strategy card. Sends a fixed
 * 200-sqft-patio sample project through the AI with the current prompt
 * and (on demand) the default prompt, then displays grand totals
 * side-by-side with a delta so the superadmin can calibrate prompts
 * without touching a real estimate.
 */
export function TestPromptPanel({ row, state, onRun }) {
    const { running, result, defaultResult, usingDefault } = state;
    const fmt = (n) =>
        "$" +
        Number(n || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    let deltaPct = null;
    let deltaAmt = null;
    if (result && defaultResult && defaultResult.grand_total > 0) {
        deltaAmt = (result.grand_total || 0) - (defaultResult.grand_total || 0);
        deltaPct = (deltaAmt / defaultResult.grand_total) * 100;
    }
    return (
        <div
            className="mt-4 border-t border-[#2A3655] pt-3"
            data-testid={`admin-prompt-test-panel-${row.id}`}
        >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Test prompt
                </span>
                <button
                    type="button"
                    onClick={() => onRun(row, false)}
                    disabled={running}
                    data-testid={`admin-prompt-test-current-${row.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-sm bg-[#00C853] hover:bg-[#00E676] text-black disabled:opacity-50 transition-colors"
                >
                    {running && !usingDefault ? "Testing…" : "▶  Run current"}
                </button>
                <button
                    type="button"
                    onClick={() => onRun(row, true)}
                    disabled={running}
                    data-testid={`admin-prompt-test-default-${row.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-sm bg-[#1C253C] border border-[#2A3655] text-slate-200 hover:bg-[#2A3655] disabled:opacity-50 transition-colors"
                >
                    {running && usingDefault ? "Testing…" : "Run default (for compare)"}
                </button>
                <span className="text-[10px] text-slate-500 ml-auto">
                    Sample: 200 sqft patio + demolition + fence · Barrie ON · ~10-20s
                </span>
            </div>

            {(result || defaultResult) && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                    <TestTile
                        label="Current prompt"
                        total={result?.grand_total}
                        lines={result?.line_count}
                        fmt={fmt}
                        color="#00C853"
                        testid={`admin-prompt-test-result-${row.id}`}
                    />
                    <TestTile
                        label="Default prompt"
                        total={defaultResult?.grand_total}
                        lines={defaultResult?.line_count}
                        fmt={fmt}
                        color="#00A3FF"
                        testid={`admin-prompt-test-default-result-${row.id}`}
                    />
                    <div
                        className={`rounded-sm border p-2 ${
                            deltaPct === null
                                ? "border-[#2A3655] bg-[#0A1128]"
                                : Math.abs(deltaPct) < 1
                                    ? "border-slate-500/40 bg-slate-500/5"
                                    : deltaPct > 0
                                        ? "border-[#FFA726]/40 bg-[#FFA726]/5"
                                        : "border-[#00C853]/40 bg-[#00C853]/5"
                        }`}
                        data-testid={`admin-prompt-test-delta-${row.id}`}
                    >
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                            Delta (current vs default)
                        </div>
                        {deltaPct === null ? (
                            <div className="text-sm text-slate-500 mt-1">
                                Run both to compare
                            </div>
                        ) : (
                            <>
                                <div
                                    className={`text-lg font-bold tabular-nums ${
                                        deltaPct > 0
                                            ? "text-[#FFA726]"
                                            : deltaPct < 0
                                                ? "text-[#00C853]"
                                                : "text-slate-300"
                                    }`}
                                >
                                    {deltaPct > 0 ? "+" : ""}
                                    {deltaPct.toFixed(1)}%
                                </div>
                                <div className="text-xs text-slate-400 tabular-nums">
                                    {deltaAmt > 0 ? "+" : ""}
                                    {fmt(deltaAmt)}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {result?.lines?.length > 0 && (
                <details className="mt-1">
                    <summary className="text-[11px] text-slate-400 cursor-pointer hover:text-white">
                        Show {result.lines.length} sample line{result.lines.length === 1 ? "" : "s"} (current prompt)
                    </summary>
                    <div className="mt-2 bg-[#0A1128] border border-[#1C253C] rounded-sm p-2 max-h-[240px] overflow-y-auto">
                        <table className="w-full text-[11px] tabular-nums">
                            <thead>
                                <tr className="text-slate-500 border-b border-[#1C253C]">
                                    <th className="text-left py-1 pr-2">Item</th>
                                    <th className="text-right py-1 px-1">Qty</th>
                                    <th className="text-left py-1 px-1">Unit</th>
                                    <th className="text-right py-1 px-1">@</th>
                                    <th className="text-right py-1 pl-1">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {result.lines.map((ln, i) => (
                                    <tr key={i} className="text-slate-300 border-b border-[#1C253C]/50 last:border-0">
                                        <td className="py-1 pr-2 truncate max-w-[260px]">
                                            {ln.name}
                                        </td>
                                        <td className="text-right py-1 px-1">
                                            {ln.quantity}
                                        </td>
                                        <td className="py-1 px-1 text-slate-500">
                                            {ln.unit}
                                        </td>
                                        <td className="text-right py-1 px-1">
                                            {fmt(ln.unit_price)}
                                        </td>
                                        <td className="text-right py-1 pl-1 font-semibold">
                                            {fmt(ln.line_total)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>
            )}
        </div>
    );
}

export function TestTile({ label, total, lines, fmt, color, testid }) {
    return (
        <div
            className="rounded-sm border p-2"
            style={{ borderColor: `${color}40`, background: `${color}08` }}
            data-testid={testid}
        >
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                {label}
            </div>
            {total !== undefined && total !== null ? (
                <>
                    <div
                        className="text-lg font-bold tabular-nums"
                        style={{ color }}
                    >
                        {fmt(total)}
                    </div>
                    <div className="text-xs text-slate-400">
                        {lines || 0} line{lines === 1 ? "" : "s"}
                    </div>
                </>
            ) : (
                <div className="text-sm text-slate-500 mt-1">
                    Not run yet
                </div>
            )}
        </div>
    );
}
